(() => {
  if (customElements.get('nny-product-card')) return;
  const KEY = 'nny-saved-formulas-v1';
  let memory = [], storageAvailable = true;
  const readSaved = () => {
    if (!storageAvailable) return memory;
    try { const stored = JSON.parse(localStorage.getItem(KEY) || '[]'); return Array.isArray(stored) ? stored.filter(p => p && typeof p.id === 'string' && typeof p.url === 'string') : []; }
    catch { storageAvailable = false; return memory; }
  };
  const saved = id => readSaved().some(p => p.id === String(id));
  const safeURL = value => { try { const u = new URL(value, location.origin); return u.origin === location.origin && /^https?:$/.test(u.protocol) ? u.href : null; } catch { return null; } };
  const writeSaved = items => {
    memory = items;
    try { localStorage.setItem(KEY, JSON.stringify(items)); } catch { storageAvailable = false; }
    document.dispatchEvent(new CustomEvent('nny:saved-change'));
  };
  const renderSaved = () => {
    const items = readSaved();
    document.querySelectorAll('[data-nny-saved-count]').forEach(el => el.textContent = items.length);
    document.querySelectorAll('nny-product-card').forEach(card => {
      const button = card.querySelector('[data-save]');
      const active = items.some(p => p.id === card.dataset.productId);
      button?.setAttribute('aria-pressed', String(active));
      button?.setAttribute('aria-label', `${active ? 'Remove' : 'Save'} ${card.dataset.title}${active ? ' from saved formulas' : ''}`);
    });
    const list = document.querySelector('[data-saved-list]');
    if (!list) return;
    list.replaceChildren();
    if (!items.length) { const p = document.createElement('p'); p.className = 'nc-dialog-note'; p.textContent = 'Tap the heart on a formula to save it here.'; list.append(p); }
    items.forEach(item => {
      const url = safeURL(item.url); if (!url) return;
      const row = document.createElement('div'); row.className = 'nc-saved-item';
      const pic = document.createElement('a'); pic.href = url; pic.setAttribute('aria-label', item.title || 'View formula');
      if (item.image) { try { const u = new URL(item.image, location.origin); if (/^https?:$/.test(u.protocol)) { const img = document.createElement('img'); img.src = u.href; img.alt = ''; img.loading = 'lazy'; pic.append(img); } } catch {} }
      const link = document.createElement('a'); link.href = url; link.textContent = item.title || 'View formula';
      const remove = document.createElement('button'); remove.type = 'button'; remove.textContent = '×'; remove.setAttribute('aria-label', `Remove ${item.title || 'formula'}`);
      remove.addEventListener('click', () => { writeSaved(readSaved().filter(p => p.id !== item.id)); list.querySelector('button')?.focus(); });
      row.append(pic, link, remove); list.append(row);
    });
  };
  document.addEventListener('nny:saved-change', renderSaved);
  window.addEventListener('storage', e => { if (e.key === KEY) document.dispatchEvent(new CustomEvent('nny:saved-change')); });
  document.addEventListener('click', e => {
    const opener = e.target.closest('[data-open-nny-saved]');
    if (opener) { const dialog = document.getElementById('NnySavedDialog'); if (dialog) { e.preventDefault(); renderSaved(); dialog.showModal(); } }
    const closer = e.target.closest('[data-dialog-close]'); closer?.closest('dialog')?.close();
    if (e.target instanceof HTMLDialogElement) { const r = e.target.getBoundingClientRect(); if (e.clientX < r.left || e.clientX > r.right || e.clientY < r.top || e.clientY > r.bottom) e.target.close(); }
  });
  customElements.define('nny-product-card', class extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.controller = new AbortController();
      const signal = this.controller.signal;
      const q = s => this.querySelector(s);
      const config = JSON.parse(q('[data-card-config]')?.textContent || '{"variants":[]}');
      const form = q('[data-card-form]'), variantSelect = q('[data-card-variant]'), planSelect = q('[data-card-plan]');
      if (!form || !variantSelect || !planSelect) return;
      let mode = this.dataset.defaultRefill === 'true' ? 'refill' : 'once', pending = false;
      const variant = () => config.variants.find(v => v.id === variantSelect.value);
      const status = q('[data-card-status]');
      const showStatus = text => { status.textContent = text; status.hidden = !text; };
      const render = (variantChanged = false) => {
        const v = variant(); if (!v) return;
        if (variantChanged) {
          const oldPlan = planSelect.value;
          planSelect.replaceChildren();
          v.plans.forEach(p => planSelect.add(new Option(p.name, p.id)));
          if (v.plans.some(p => p.id === oldPlan)) planSelect.value = oldPlan;
          q('[data-card-quantity]').value = v.min;
          if (q('[data-variant-volume]')) q('[data-variant-volume]').textContent = v.title;
          this.dataset.price = v.priceCents / 100;
          this.dispatchEvent(new CustomEvent('nny:variant-change', {bubbles:true}));
        }
        if (v.requiresPlan) mode = 'refill';
        else if (!v.plans.length) mode = 'once';
        const plan = v.plans.find(p => p.id === planSelect.value) || v.plans[0];
        const isRefill = mode === 'refill' && !!plan;
        q('[data-modes]').hidden = !v.plans.length || !v.available;
        q('[data-mode="once"]').hidden = v.requiresPlan;
        q('[data-mode="once"]').setAttribute('aria-pressed', String(!isRefill));
        q('[data-mode="refill"]').setAttribute('aria-pressed', String(isRefill));
        q('[data-once-price]').textContent = v.price;
        q('[data-refill-price]').textContent = plan?.price || '';
        const percent = plan && v.priceCents > plan.priceCents ? Math.round((1 - plan.priceCents / v.priceCents) * 100) : 0;
        q('[data-discount]').hidden = !percent;
        q('[data-discount]').textContent = `-${percent}%`;
        q('[data-single-price]').hidden = v.plans.length > 0 && v.available;
        q('[data-single-price]').textContent = v.available ? v.price : `SOLD OUT · ${v.price}`;
        q('[data-cadence]').hidden = !isRefill || !v.available;
        planSelect.disabled = !isRefill;
        q('[data-recurring]').hidden = !isRefill || !v.available;
        q('[data-sold]').hidden = v.available;
        if (q('[data-badge]')) q('[data-badge]').hidden = !v.available;
        q('[data-restock]').hidden = v.available;
        const add = q('[data-card-add]'); add.hidden = !v.available;
        add.disabled = pending || !v.available || (v.requiresPlan && !plan);
        add.textContent = pending ? 'ADDING…' : `+ ${isRefill ? this.dataset.refillLabel || 'REFILL' : this.dataset.addLabel || 'ADD'} • ${isRefill ? plan.price : v.price}${v.min > 1 ? ` × ${v.min}` : ''}`;
      };
      variantSelect.addEventListener('change', () => { showStatus(''); render(true); }, {signal});
      planSelect.addEventListener('change', () => render(), {signal});
      this.querySelectorAll('[data-mode]').forEach(button => button.addEventListener('click', () => { mode = button.dataset.mode; render(); }, {signal}));
      q('[data-save]').addEventListener('click', () => {
        const items = readSaved(), id = this.dataset.productId;
        if (items.some(p => p.id === id)) writeSaved(items.filter(p => p.id !== id));
        else writeSaved([...items, {id, title:this.dataset.title, url:this.dataset.productUrl, image:q('.nc-photo--first')?.currentSrc || q('.nc-photo--first')?.src || ''}]);
      }, {signal});
      q('[data-angle]')?.addEventListener('click', e => {
        const second = this.classList.toggle('is-angle-two');
        e.currentTarget.setAttribute('aria-pressed', String(second));
        e.currentTarget.setAttribute('aria-label', `Show ${second ? 'first' : 'second'} product photo`);
        q('[data-angle-label]').textContent = second ? 'Angle 2' : 'Angle 1';
      }, {signal});
      this.addEventListener('click', e => {
        if (!e.target.closest('a,button,input,select,label,form') && !window.getSelection()?.toString()) location.assign(this.dataset.productUrl);
      }, {signal});
      q('.nc-photo-link')?.addEventListener('pointerenter', () => { if (q('[data-angle-label]') && matchMedia('(hover:hover)').matches) q('[data-angle-label]').textContent = 'Angle 2'; }, {signal});
      q('.nc-photo-link')?.addEventListener('pointerleave', () => { if (q('[data-angle-label]')) q('[data-angle-label]').textContent = this.classList.contains('is-angle-two') ? 'Angle 2' : 'Angle 1'; }, {signal});
      q('[data-restock]').addEventListener('click', e => {
        const dialog = document.getElementById('NnyRestockDialog'); if (!dialog) return;
        e.preventDefault(); const v = variant();
        dialog.querySelector('[data-restock-product]').value = `${this.dataset.title} — ${v.title}`;
        dialog.querySelector('[data-restock-body]').value = `Restock update requested for ${this.dataset.title}, variant ${v.id} (${v.title}). Product: ${new URL(this.dataset.productUrl,location.origin).href}`;
        dialog.showModal(); dialog.querySelector('[name="contact[email]"]').focus();
      }, {signal});
      form.addEventListener('submit', async e => {
        e.preventDefault(); const v = variant();
        if (pending || !v?.available || !form.reportValidity()) return;
        if (v.requiresPlan && !v.plans.length) { showStatus('Choose a purchase option on the product page.'); return; }
        pending = true; render(); q('[data-card-add]').setAttribute('aria-busy','true'); showStatus('');
        let added = false;
        try {
          const fields = new FormData(form); fields.delete('return_to');
          const response = await fetch(this.dataset.addUrl, {method:'POST',credentials:'same-origin',headers:{Accept:'application/json'},body:fields});
          const result = await response.json();
          if (!response.ok) throw new Error(result.description || result.message || 'Unable to add this formula. Please try again.');
          added = true;
          if (typeof window.nnyOpenCartDrawer === 'function') {
            const refreshed = await window.nnyOpenCartDrawer();
            showStatus(refreshed === false ? 'Added. Reopen your bag to refresh it.' : '✓ Added to your bag.');
          } else showStatus('✓ Added. Open your bag to continue.');
        } catch (error) { showStatus(added ? 'Added. Open your bag to continue.' : error.message); }
        finally { pending = false; q('[data-card-add]').removeAttribute('aria-busy'); render(); }
      }, {signal});
      render(true); renderSaved();
    }
    refresh() {
      this.controller?.abort();
      this.initialized = false;
      this.connectedCallback();
    }
    disconnectedCallback() {
      queueMicrotask(() => {
        if (!this.isConnected) { this.controller?.abort(); this.initialized = false; }
      });
    }
  });
  customElements.define('nny-catalog', class extends HTMLElement {
    connectedCallback() {
      if (this.abort) return;
      this.abort = new AbortController(); const signal = this.abort.signal;
      const q = s => this.querySelector(s), all = s => [...this.querySelectorAll(s)];
      const grid = q('.nny-products-grid'); if (!grid) return;
      const cards = all('nny-product-card');
      cards.forEach((card,i) => card.dataset.originalOrder = i);
      let category = 'all', ingredient = 'all', savedOnly = false;
      const progress = () => {
        const max = grid.scrollWidth - grid.clientWidth;
        const next = q('[data-catalog-next]'); if (next) next.disabled = max <= 2 || grid.scrollLeft >= max - 3;
        const bar = q('#nnyCatalogScrollProgress'); if (bar) bar.style.width = `${max > 0 ? Math.max(10, Math.min(100, grid.scrollLeft / max * 100)) : 100}%`;
        const prev = q('#nnyScrollLeft'), right = q('#nnyScrollRight');
        if (prev) prev.disabled = grid.scrollLeft <= 2;
        if (right) right.disabled = max <= 2 || grid.scrollLeft >= max - 3;
      };
      const filter = (reset = true) => {
        const term = q('#nnyCatalogSearchInput')?.value.toLowerCase().trim() || '';
        const candidates = cards.filter(c => (category === 'all' || c.dataset.category === category) && (!savedOnly || saved(c.dataset.productId)) && (!term || `${c.dataset.title} ${c.dataset.ingredients}`.toLowerCase().includes(term)));
        const matches = candidates.filter(c => ingredient === 'all' || c.dataset.ingredients.includes(ingredient));
        cards.forEach(c => { c.hidden = !matches.includes(c); });
        all('[data-ing]').forEach(button => { const counter = button.querySelector('.nny-ing-count'); if (counter) counter.textContent = candidates.filter(c => button.dataset.ing === 'all' || c.dataset.ingredients.includes(button.dataset.ing)).length; });
        if (q('[data-catalog-count]')) q('[data-catalog-count]').textContent = matches.length;
        if (q('[data-catalog-empty]')) q('[data-catalog-empty]').hidden = matches.length > 0;
        if (reset) grid.scrollLeft = 0;
        requestAnimationFrame(progress);
      };
      all('[data-cat]').forEach(b => b.addEventListener('click', () => { category = b.dataset.cat; all('[data-cat]').forEach(c => { c.classList.toggle('is-active',c===b); c.setAttribute('aria-pressed',String(c===b)); }); filter(); }, {signal}));
      all('[data-ing]').forEach(b => b.addEventListener('click', () => { ingredient = b.dataset.ing; all('[data-ing]').forEach(c => { c.classList.toggle('is-active',c===b); c.setAttribute('aria-pressed',String(c===b)); }); filter(); }, {signal}));
      q('#nnyCatalogSearchInput')?.addEventListener('input', () => filter(), {signal});
      q('[data-saved-filter]')?.addEventListener('click', e => { savedOnly = !savedOnly; e.currentTarget.setAttribute('aria-pressed',String(savedOnly)); filter(); }, {signal});
      q('#nnyCatalogSort')?.addEventListener('change', e => {
        const sorted = [...cards].sort((a,b) => e.target.value === 'featured' ? a.dataset.originalOrder-b.dataset.originalOrder : (a.dataset.price-b.dataset.price)*(e.target.value==='price-asc'?1:-1));
        sorted.forEach(card => grid.append(card)); filter();
      }, {signal});
      const scroll = direction => grid.scrollBy({left:direction*(cards.find(c=>!c.hidden)?.getBoundingClientRect().width+16 || 290),behavior:matchMedia('(prefers-reduced-motion: reduce)').matches?'auto':'smooth'});
      q('#nnyScrollLeft')?.addEventListener('click',()=>scroll(-1),{signal});
      [q('#nnyScrollRight'),q('[data-catalog-next]')].forEach(b => b?.addEventListener('click',()=>scroll(1),{signal}));
      ['Grid','List'].forEach(mode=>q(`#nnyView${mode}`)?.addEventListener('click',()=>{grid.classList.toggle('is-list',mode==='List');['Grid','List'].forEach(m=>q(`#nnyView${m}`)?.classList.toggle('is-active',m===mode));progress();},{signal}));
      grid.addEventListener('scroll', progress, {...({passive:true,signal}),signal});
      document.addEventListener('nny:saved-change',()=>filter(false),{signal});
      this.addEventListener('nny:variant-change',()=>filter(false),{signal});
      this.resize = new ResizeObserver(progress); this.resize.observe(grid);
      filter();
    }
    refresh() { this.disconnectedCallback(); this.connectedCallback(); }
    disconnectedCallback() { this.abort?.abort(); this.abort = null; this.resize?.disconnect(); }
  });
  renderSaved();
  if (document.querySelector('[data-restock-result]')) document.getElementById('NnyRestockDialog')?.showModal();
})();
